import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { eq } from "../../utils/math";
import { toLocaleLengthString } from "../../utils/number";
import { quaternionFromAxesToRef } from "../utils/MathUtils";
import { resizeInViewSpace } from "../utils/MeshUtils";
import { ViewLayerMaskFlags } from "../view/View";
export class LengthFeedback {
    constructor(pPosIndex, pRefPoint, pAxis, pColor, pNormal, pScene, pView, pWorlMatrix, pShowStartArrowHead) {
        this.mAxis = pAxis;
        this.mView = pView;
        this.mScene = pScene;
        this.mRefPoint = pRefPoint;
        this.mPosIndex = pPosIndex;
        this.mNormal = pNormal;
        this.mWorldMatrix = pWorlMatrix;
        this.mApp = this.mView.viewer.app;
        this.showAbsoluteValues = false;
        if (this.mWorldMatrix != null) {
            this.mRefPoint = Vector3.TransformCoordinates(this.mRefPoint, this.mWorldMatrix);
            this.mAxis = Vector3.TransformNormal(this.mAxis, this.mWorldMatrix);
            this.mAxis.normalize();
            this.mNormal = Vector3.TransformNormal(this.mNormal, this.mWorldMatrix);
            this.mNormal.normalize();
        }
        this.mValueDiv = document.createElement("div");
        this.mValueDiv.className = "wcf-core length-feedback";
        this.mValueDiv.style.borderColor = pColor;
        document.body.appendChild(this.mValueDiv);
        this.mMaterial = new PBRMaterial("", this.mScene);
        this.mMaterial.albedoColor = Color3.Black();
        this.mMaterial.emissiveColor = Color3.FromHexString(pColor).toLinearSpace();
        this.mMaterial.disableLighting = true;
        this.mMaterial.unlit = true;
        this.mMaterial.backFaceCulling = false;
        this.mMaterial.freeze();
        this.mArrowHead = CreateDisc("", { tessellation: 3, radius: 1 }, this.mScene);
        this.mArrowHead.bakeTransformIntoVertices(Matrix.Translation(-1, 0, 0));
        this.mArrowHead.renderingGroupId = 2;
        this.mArrowHead.rotationQuaternion = new Quaternion();
        this.mArrowHead.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        this.mArrowHead.isPickable = false;
        this.mArrowHead.material = this.mMaterial;
        this.mArrowHead.setEnabled(false);
        this.mArrowHeadStart = null;
        if (pShowStartArrowHead) {
            this.mArrowHeadStart = CreateDisc("", { tessellation: 3, radius: 1 }, this.mScene);
            this.mArrowHeadStart.bakeTransformIntoVertices(Matrix.Translation(-1, 0, 0));
            this.mArrowHeadStart.bakeTransformIntoVertices(Matrix.RotationY(Math.PI));
            this.mArrowHeadStart.renderingGroupId = 2;
            this.mArrowHeadStart.rotationQuaternion = new Quaternion();
            this.mArrowHeadStart.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
            this.mArrowHeadStart.isPickable = false;
            this.mArrowHeadStart.material = this.mMaterial;
            this.mArrowHeadStart.position = this.mRefPoint;
            this.mArrowHeadStart.setEnabled(false);
        }
        const tDummyPos = Vector3.Zero();
        this.mLine1 = CreateLines("", { points: [tDummyPos, tDummyPos], updatable: true, instance: null, useVertexAlpha: false }, this.mScene);
        this.mLine1.color = Color3.FromHexString(pColor);
        this.mLine1.renderingGroupId = 2;
        this.mLine1.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        this.mLine1.isPickable = false;
        this.mLine1.setEnabled(false);
        this.mLine1PosData = this.mLine1.getVerticesData(VertexBuffer.PositionKind, false);
        this.mLine2 = CreateLines("", { points: [tDummyPos, tDummyPos], updatable: true, instance: null }, this.mScene);
        this.mLine2.color = Color3.FromHexString("#868686");
        this.mLine2.alpha = 0.5;
        this.mLine2.renderingGroupId = 1;
        this.mLine2.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        this.mLine2.isPickable = false;
        this.mLine2.setEnabled(false);
        this.mLine2PosData = this.mLine2.getVerticesData(VertexBuffer.PositionKind, false);
    }
    update(pPointer, pCurrentPoint) {
        //resize arrow only
        if (pCurrentPoint == null) {
            resizeInViewSpace(this.mView, this.mArrowHead, 1, 6);
            if (this.mArrowHeadStart != null) {
                resizeInViewSpace(this.mView, this.mArrowHeadStart, 1, 6);
            }
            return;
        }
        let tCurrentPoint = pCurrentPoint;
        if (this.mWorldMatrix != null) {
            tCurrentPoint = Vector3.TransformCoordinates(tCurrentPoint, this.mWorldMatrix);
        }
        const tVDelta = tCurrentPoint.subtract(this.mRefPoint);
        tVDelta.multiplyInPlace(this.mAxis);
        const tDistance = tVDelta.x + tVDelta.y + tVDelta.z; //2 of the 3 components should be zero
        let tOffset = 30; //touch, pen
        if ((pPointer != null && pPointer.isMousePointer) || this.mView.viewer.inputState.pointerType === "mouse")
            tOffset = 10;
        let tDivValue = tDistance;
        if (this.showAbsoluteValues) {
            tDivValue = Math.abs(tDivValue);
        }
        const tPointerPos = pPointer != null ? pPointer.position : this.mView.viewer.inputState.pointerPosition;
        this.mValueDiv.textContent = toLocaleLengthString(tDivValue, this.mApp.i18n.locale, this.mApp.i18n.lengthUnit);
        const tPosX = tPointerPos.x - this.mValueDiv.offsetWidth - tOffset;
        const tPosY = tPointerPos.y - this.mValueDiv.offsetHeight - tOffset -
            this.mPosIndex * (this.mValueDiv.offsetHeight + 5);
        this.mValueDiv.style.top = tPosY.toString() + "px";
        this.mValueDiv.style.left = tPosX.toString() + "px";
        if (eq(tDistance, 0)) {
            this.mArrowHead.setEnabled(false);
            if (this.mArrowHeadStart != null) {
                this.mArrowHeadStart.setEnabled(false);
            }
            this.mLine1.setEnabled(false);
            this.mLine2.setEnabled(false);
        }
        else {
            let tLineP1 = this.mRefPoint;
            let tLineP2 = this.mRefPoint.add(this.mAxis.scale(tDistance));
            //update arrow heads
            const tAxisX = tLineP2.subtract(tLineP1).normalize();
            const tAxisZ = this.mNormal;
            const tAxisY = Vector3.Cross(tAxisZ, tAxisX);
            this.mArrowHead.position = tLineP2;
            quaternionFromAxesToRef(tAxisX, tAxisY, tAxisZ, this.mArrowHead.rotationQuaternion);
            this.mArrowHead.setEnabled(true);
            resizeInViewSpace(this.mView, this.mArrowHead, 1, 6);
            if (this.mArrowHeadStart != null) {
                this.mArrowHeadStart.rotationQuaternion = this.mArrowHead.rotationQuaternion;
                this.mArrowHeadStart.setEnabled(true);
                resizeInViewSpace(this.mView, this.mArrowHeadStart, 1, 6);
            }
            //update lines
            this.mLine1PosData[0] = tLineP1.x;
            this.mLine1PosData[1] = tLineP1.y;
            this.mLine1PosData[2] = tLineP1.z;
            this.mLine1PosData[3] = tLineP2.x;
            this.mLine1PosData[4] = tLineP2.y;
            this.mLine1PosData[5] = tLineP2.z;
            this.mLine1.updateVerticesData(VertexBuffer.PositionKind, this.mLine1PosData, true, false);
            this.mLine1.setEnabled(true);
            tLineP1 = tCurrentPoint;
            tLineP2 = tCurrentPoint.subtract(this.mAxis.scale(tDistance));
            this.mLine2PosData[0] = tLineP1.x;
            this.mLine2PosData[1] = tLineP1.y;
            this.mLine2PosData[2] = tLineP1.z;
            this.mLine2PosData[3] = tLineP2.x;
            this.mLine2PosData[4] = tLineP2.y;
            this.mLine2PosData[5] = tLineP2.z;
            this.mLine2.updateVerticesData(VertexBuffer.PositionKind, this.mLine2PosData, true, false);
            this.mLine2.setEnabled(true);
        }
    }
    dispose() {
        document.body.removeChild(this.mValueDiv);
        if (this.mLine1 != null) {
            this.mLine1.dispose();
            this.mLine1 = undefined;
        }
        if (this.mLine2 != null) {
            this.mLine2.dispose();
            this.mLine2 = undefined;
        }
        this.mArrowHead.dispose();
        if (this.mArrowHeadStart != null) {
            this.mArrowHeadStart.dispose();
        }
        this.mMaterial.dispose();
    }
}
